import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import MainLayout from '../../mainLayout';
import styled, {useTheme} from 'styled-components/native';
import {I18nManager, Platform} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {RouteProp, useRoute} from '@react-navigation/native';
import {connect} from 'react-redux';
import {BottomSheetScrollView} from '@gorhom/bottom-sheet';
import Config from 'react-native-config';
import Animated, {
  Easing,
  FadeIn,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

// Components
import TextGeneric from '../../../components/textGeneric';
import CustomScrollView from '../../../components/customScrollView';
import Icon from '../../../components/icon';
import TableView from '../../../components/tableView';
import LatestArticleSkeleton from '../../../components/latestArticleSkeleton';
import EmptyList from '../../../components/emptyList';
import GenericButton from '../../../components/button';
import AutoHeightWebView from '../../../components/autoHeightWebView';
import LanguagePickerModal from '../../../connected-components/languagePickerModal';
import {EmptyContainer, Space} from '../home';
import {TibButton} from '../../../components/tibButton';

// Utils & Services
import Share from '../../../utils/share';
import {Theme} from '../../../theme/lightTheme';
import {tr} from '../../../translations';
import {showGlobalToast} from '../../../connected-components/toast/actions';
import {
  getSettingByKey,
  isLanguageRTL,
  isUrl,
  openUrl,
  removeHtmlTags,
} from '../../../utils/helpers';
import {
  finishLoading,
  startLoading,
} from '../../../connected-components/loading/actions';
import {translateTableArticle} from '../../../utils/services/translation';
import {openLanguagePickerModal} from '../../../connected-components/languagePickerModal/actions';
import {analyticsService} from '../../../utils/services/analytics';
import {renderArticleContent} from '../../../utils/html';
import {useTTSState} from '../../../utils/services/TTSService/useTTSState';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';
import {toggleSettingsModal} from '../../../connected-components/settingsModal/actions';
import {stopPlayer} from '../../../utils/services/playerService';

// Config & Data
import screens from '../../../config/screens';
import {RootState} from '../../../redux/store';
import {LatestArticleProps} from '../../../interfaces/screens/latestArticle';
import {LatestArticleRouteParams} from '../../../interfaces/api/content/home';
import {
  GenerateSharedLinkBody,
  NewsItemDetailsType,
  TableLabelTranslations,
} from '../../../interfaces/api/tabs';
import {
  AnalyticsEvents,
  deepLinkingTypes,
  NewsOptionTab,
  SettingKey,
  ToastType,
} from '../../../utils/enums';

// Actions
import {generateShareLink, getNewsDetails} from './actions';
import {
  getAnalysisDetails,
  getPredictionDetails,
  getSummaryDetails,
} from '../summaries/actions';
import {checkIfFavorite, toggleFavorite} from '../favorite/actions';
import {getByScreenSize, isWeb} from '../../../utils/responsive';
import '../../../utils/webAnimations';

// Styled Components
const Container = styled.View<{responsiveWidth: string}>`
  width: ${({responsiveWidth}) => responsiveWidth};
  height: ${Platform.OS === 'web' ? '100vh' : '100%'};
  align-self: center;
`;

const ContentContainer = styled(CustomScrollView)`
  width: 100%;
  flex: 1;
  align-self: center;
`;

const Title = styled(TextGeneric)`
  color: ${({theme}) => theme.text.dark};
  font-size: ${({theme, font}) => (font?.fontSize || theme.text.s7) + 2}px;
  line-height: ${({theme, font}) =>
    ((font?.fontSize || theme.text.s7) + 2) * 1.3}px;
  text-align: ${({forceRTL}) => (forceRTL ? 'right' : 'left')};
  padding-bottom: ${isWeb ? '2%' : '5%'};
  padding-left: ${I18nManager.isRTL ? '7%' : 0};
  padding-right: ${I18nManager.isRTL ? 0 : '7%'};
`;

const Row = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const IconsContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 5%;
  width: 100%;
`;

const AddNoteButton = styled.TouchableOpacity`
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`;

const IconContainer = styled.TouchableOpacity<{
  isSmall?: boolean;
  isPrimary?: boolean;
}>`
  width: ${({theme, isSmall}) => theme.text.s1 * (isSmall ? 1 : 1.1)}px;
  height: ${({theme, isSmall}) => theme.text.s1 * (isSmall ? 1 : 1.1)}px;
  background-color: ${({theme, isPrimary}) =>
    isPrimary ? theme.primary : theme.text.light};
  border-radius: 100px;
  align-items: center;
  justify-content: center;
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 2px 4px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
`;

const AddNoteText = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s6}px;
  color: ${({theme}) => theme.text.dark + 'EE'};
  margin-left: ${({theme}) => theme.text.s55 / 2}px;
`;

/**
 * Default table translations used for the article metadata
 */
const tableTranslations: TableLabelTranslations = {
  specialty: 'Specialty',
  subspecialty: 'Topic',
  url: 'Link - Reference',
  publisher: 'The Publisher',
  resource: 'Resources',
  date: 'The Date',
  origin: 'Origin',
  readabilityscore: 'Readability score',
  sentiment: 'Sensation',
  accesslevel: 'Access Level',
  type_of_study_value: 'Type of the study',
  summary: 'Summary',
  viewMore: 'View More',
  successMessage:
    'The content has been translated for your convenience using Google Translate',
};

// After imports, let's define interface types for our components

interface FavoriteButtonContainerProps {
  item: any;
  getHeaderTitle: string;
  isRecommendation: boolean;
  isRecommendationByTab: boolean;
  theme: Theme;
  initialFavorite: boolean;
}

interface PlayButtonContainerProps {
  tableTranslationData: any;
  language: string;
  theme: Theme;
}

/**
 * FavoriteButtonContainer - Completely isolated favorite button component with its own state
 */
const FavoriteButtonContainer = React.memo(
  ({
    item,
    getHeaderTitle,
    isRecommendation,
    isRecommendationByTab,
    theme,
    initialFavorite = false,
  }: FavoriteButtonContainerProps) => {
    const [isFavorite, setFavorite] = useState(initialFavorite);

    const onFavoritePressHandler = useCallback(() => {
      setFavorite((prevState: boolean) => !prevState);
      toggleFavorite({
        ...item,
        isSummary: getHeaderTitle,
        isRecommendation,
        isRecommendationByTab,
      });
    }, [item, getHeaderTitle, isRecommendation, isRecommendationByTab]);

    return (
      <IconContainer
        style={{marginLeft: theme.text.s12 / 2}}
        key="favorite"
        onPress={onFavoritePressHandler}
        isSmall
        isPrimary>
        <Icon
          name={isFavorite ? 'heart' : 'hearto'}
          size={theme.text.s55}
          color={theme.text.light}
        />
      </IconContainer>
    );
  },
);

/**
 * PlayButtonContainer - Completely isolated play button component with its own state
 */
const PlayButtonContainer = React.memo(
  ({tableTranslationData, language, theme}: PlayButtonContainerProps) => {
    const {isReading, startReading, stopReading} = useTTSState();

    useEffect(() => {
      return () => {
        stopReading();
      };
    }, []);

    const onPlayPressHandler = useCallback(() => {
      if (isReading) {
        stopReading();
      } else {
        const textContent = removeHtmlTags(
          tableTranslationData.description || tableTranslationData.summary,
        );
        startReading(textContent);
      }
    }, [isReading, stopReading, startReading, tableTranslationData]);

    if (language !== 'en') return null;

    return (
      <IconContainer
        style={{marginLeft: theme.text.s12 / 2}}
        key="play"
        onPress={onPlayPressHandler}
        isSmall>
        <Icon
          name={isReading ? 'pause' : 'play'}
          size={theme.text.s55}
          color={theme.primary}
        />
      </IconContainer>
    );
  },
);

/**
 * TipButtonContainer - Completely isolated tip button component with its own state
 */
const TipButtonContainer = React.memo(() => {
  const [isTipVisible, setIsTipVisible] = useState(false);
  const {wdp, getByScreenSize} = useResponsiveDimensions();
  const theme = useTheme();

  const handleDismissTip = useCallback(() => {
    setIsTipVisible(false);
  }, []);

  const disclaimerButton = (
    <GenericButton
      label={tr('latest.disclaimer')}
      labelStyle={{
        color: theme.text.dark,
        fontSize: theme.text.s6,
      }}
      onPress={() => setIsTipVisible(true)}
      style={{
        marginVertical: getByScreenSize(wdp(5), wdp(2)),
        marginHorizontal: getByScreenSize(wdp(3), wdp(2)),
        backgroundColor: theme.primary + '33',
      }}
      showRightIcon
      rightIconName={'info'}
      rightIconColor={theme.primary}
      rightIconSize={theme.text.s3}
    />
  );

  return (
    <>
      <Animated.View
        {...(Platform.OS === 'web'
          ? {className: 'screen-fade-in'}
          : {entering: FadeIn.delay(500).duration(300)})}
        style={{
          alignSelf: 'flex-end',
          width: getByScreenSize('50%', '25%'),
          marginHorizontal: theme.text.s55,
        }}>
        {disclaimerButton}
      </Animated.View>

      {isTipVisible && (
        <TibButton
          isVisible={true}
          onDismiss={handleDismissTip}
          tipText={getSettingByKey(SettingKey.Disclaimer) || tr('latest.tip')}
        />
      )}
    </>
  );
});

/**
 * LatestArticle Component - Displays medical article content with various interactive features
 *
 * @param {Object} props - Component props
 * @param {Object} props.navigation - Navigation object from React Navigation
 * @param {Object} props.auth - Authentication state from Redux
 * @returns {React.ReactElement} LatestArticle component
 */
const LatestArticle: React.FC<LatestArticleProps> = ({navigation, auth}) => {
  // Hooks and Context
  const theme = useTheme() as Theme;
  const {wdp, hdp} = useResponsiveDimensions();
  const {bottom: bottomInset} = useSafeAreaInsets();
  const {currentUser} = auth || {};
  const {font} = auth?.config || {};

  // Route parameters
  const {
    item = {},
    isRecommendation = false,
    isRecommendationByTab = false,
    isSummary: isSummaryParam = '',
  } = useRoute<RouteProp<LatestArticleRouteParams>>().params || {
    item: {},
    isRecommendation: false,
    isRecommendationByTab: false,
    isSummary: '',
  };

  // State
  const getHeaderTitle = useMemo(
    () => (isSummaryParam || '')?.replace(/"/g, ''),
    [isSummaryParam],
  );
  const [language, setLanguage] = useState<string>('en');
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState<NewsItemDetailsType>({});
  const [initialFavoriteState, setInitialFavoriteState] = useState(false);
  const [tableTranslationData, setTableTranslationData] = useState<any>({
    ...(item || {}),
  });

  // Refs
  const isFirstTranslateRef = useRef(true);
  const didAnimateRef = useRef(false);
  const entryTime = useRef(new Date());
  const usedLanguagesRef = useRef<string[]>(['English']);

  // Derived state
  const isAppleUser = useMemo(
    () => currentUser?.email?.trim().toLowerCase() === Config.APPLE_EMAIL,
    [currentUser?.email],
  );

  // Animation values
  const contentOpacity = useSharedValue(0);
  const contentTranslateY = useSharedValue(20);
  const headerScale = useSharedValue(0.95);

  // Animation styles
  const headerAnimStyle = useAnimatedStyle(() => ({
    transform: isWeb ? [] : [{scale: headerScale.value}],
    opacity: isWeb ? 1 : headerScale.value,
  }));

  const contentAnimStyle = useAnimatedStyle(() => ({
    opacity: isWeb ? 1 : contentOpacity.value,
    transform: isWeb ? [] : [{translateY: contentTranslateY.value}],
  }));

  // Analytics tracking
  useEffect(() => {
    return () => {
      const timeSpent = entryTime.current
        ? (new Date().getTime() - entryTime.current.getTime()) / 1000
        : 0;

      let eventName = AnalyticsEvents.NewsArticleCompleted;
      if (isRecommendation) {
        eventName = AnalyticsEvents.RecommendationArticleCompleted;
      } else if (getHeaderTitle) {
        eventName = AnalyticsEvents.SummaryArticleCompleted;
      }

      const usedLanguages = [...new Set(usedLanguagesRef.current)];
      analyticsService?.logEvent(eventName, {
        articleId: item?.id,
        articleTitle: getHeaderTitle
          ? `GenioMD ${getHeaderTitle}`
          : item?.title,
        timeSpent,
        usedLanguages,
      });
    };
  }, []);

  // Check article favorite status
  const checkFavorite = useCallback(async () => {
    if (!item?.id) return;
    const check = await checkIfFavorite(item);
    setInitialFavoriteState(check);
  }, [item]);

  // Animation sequence
  const runEntryAnimations = useCallback(() => {
    if (isWeb) {
      // On web, use CSS animations instead of reanimated
      return;
    }
    contentOpacity.value = 0;
    contentTranslateY.value = 20;
    headerScale.value = 0.95;

    requestAnimationFrame(() => {
      headerScale.value = withTiming(1, {
        duration: 400,
        easing: Easing.bezier(0.25, 0.1, 0.25, 1),
      });

      setTimeout(() => {
        contentOpacity.value = withTiming(1, {
          duration: 400,
          easing: Easing.bezier(0.25, 0.1, 0.25, 1),
        });

        contentTranslateY.value = withTiming(0, {
          duration: 400,
          easing: Easing.out(Easing.cubic),
        });
      }, 150);
    });
  }, []);

  // Fetch article data
  const initPage = useCallback(async () => {
    checkFavorite();
    if (!item?.id) return;

    setIsLoading(true);
    setIsError(false);

    try {
      let result = null;

      if (getHeaderTitle) {
        if (getHeaderTitle === NewsOptionTab.Analysis) {
          result = await getAnalysisDetails(item?.id);
        } else if (getHeaderTitle === NewsOptionTab.Predictions) {
          result = await getPredictionDetails(item?.id);
        } else {
          result = await getSummaryDetails(item?.id);
        }
      } else {
        result = await getNewsDetails(item?.id, isRecommendationByTab);
      }

      if (!result) {
        setIsError(true);
      } else {
        setData(result);
        setTimeout(() => {
          if (!didAnimateRef.current) {
            runEntryAnimations();
            didAnimateRef.current = true;
          }
        }, 50);
      }
    } catch (error) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [
    item?.id,
    getHeaderTitle,
    isRecommendationByTab,
    runEntryAnimations,
    checkFavorite,
  ]);

  // Initialize on mount
  useEffect(() => {
    initPage();
    return () => {
      stopPlayer();
    };
  }, [initPage]);

  // Content translation
  const changeLanguageHandler = useCallback(
    async (newData: NewsItemDetailsType) => {
      if (!newData || Object.keys(newData || {}).length === 0) return;

      try {
        if (language !== 'en') {
          startLoading();
          setIsLoading(true);
        }

        const titleContent = {
          title: getHeaderTitle
            ? `GenioMD ${
                getHeaderTitle === NewsOptionTab.Predictions
                  ? 'Future Outcomes'
                  : getHeaderTitle === NewsOptionTab.Analysis
                    ? 'Analysis'
                    : 'Briefs'
              }`
            : newData.title || item?.title,
        };

        // Fields to exclude from translation
        const excludeFields = [
          'id',
          'url',
          'is_favorite',
          'publisher',
          'publishdate',
          'readabilityscore',
          'created_at',
          'creationdate',
          'date',
          'specialty_id',
          'subspecialty_id',
          'type_of_study_id',
          'resource_id',
          'tab_id',
          'img',
          'sentiment_id',
          'normal_summarization',
          'subspeciality',
          'summary_id',
          'summarydate',
          'typeofstudiesandreaserch',
          'title',
          ...(getHeaderTitle
            ? ['origin', 'resource', 'specialty', 'subspecialty']
            : []),
        ];

        // Translate all content in parallel
        const [titleTrans, contentTrans, labelsTrans] = await Promise.all([
          translateTableArticle(language, titleContent),
          translateTableArticle(language, newData, excludeFields),
          translateTableArticle(language, {...(tableTranslations || {})}),
        ]);

        if (titleTrans && contentTrans && labelsTrans) {
          // Apply translations with animation
          setTableTranslationData({
            ...contentTrans,
            tableTranslations: {
              ...labelsTrans,
              publisher:
                language === 'en' ? 'Publisher' : labelsTrans?.publisher,
              date: language === 'en' ? 'Date' : labelsTrans?.date,
            },
            title: titleTrans?.title || item?.title,
          });

          // Show toast for translations after first one
          if (!isFirstTranslateRef.current) {
            showGlobalToast({
              message:
                labelsTrans?.successMessage ||
                'The content has been translated for your convenience using Google Translate.',
              type: ToastType.HELP,
            });
          }
          isFirstTranslateRef.current = false;
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
        finishLoading();
      }
    },
    [language, getHeaderTitle, item?.title],
  );

  // Trigger translation when language changes
  useEffect(() => {
    if (data && Object.keys(data || {}).length > 0) {
      changeLanguageHandler(data);
    }
  }, [language, data, changeLanguageHandler]);

  // Sharing functionality
  const onSharePressHandler = useCallback(async () => {
    try {
      let url = `${Config.UNIVERSAL_DEEP_LINK_SCHEME}/${deepLinkingTypes.sharedArticle}/`;

      const body: GenerateSharedLinkBody = {
        model_id: item?.id,
        shared_via: Platform.OS,
        type:
          isRecommendation || isRecommendationByTab
            ? 'Recommendation'
            : getHeaderTitle
              ? 'Summary'
              : 'Article',
      };

      const token = await generateShareLink(body);
      if (token) {
        url += token;
        await Share.open({
          title: 'Check news details',
          message: url,
        });
      }
    } catch (e) {
    }
  }, [item?.id, isRecommendation, isRecommendationByTab, getHeaderTitle]);

  // Language picker
  const openLanguagePicker = useCallback(() => {
    openLanguagePickerModal({
      onSelectionChange: lang => {
        setLanguage(lang.code);
        usedLanguagesRef.current = [...usedLanguagesRef.current, lang.name];
      },
      currentValue: language,
    });
  }, [language]);

  // Article header with action buttons
  const renderArticleHeader = useCallback(() => {
    // All buttons removed - return null to remove empty space
    return null;
    
    // const nonInteractiveActions = [
    //   // {
    //   //   name: 'share',
    //   //   onPress: onSharePressHandler,
    //   //   key: 'share',
    //   //   isPrimary: false,
    //   // },
    //   // currentUser?.email?.trim().toLowerCase() !== Config.APPLE_EMAIL && {
    //   //   name: 'translate',
    //   //   onPress: openLanguagePicker,
    //   //   key: 'translate',
    //   //   isPrimary: false,
    //   // },
    // ].filter(Boolean) as {
    //   name: string;
    //   onPress: () => void;
    //   key: string;
    //   isPrimary: boolean;
    // };

    // return (
    //   <Animated.View
    //     style={headerAnimStyle}
    //     {...(Platform.OS === 'web' ? {className: 'screen-fade-in'} : {})}>
    //     <IconsContainer style={{width: '100%', alignSelf: 'center', justifyContent: 'flex-end'}}>
    //       {/* <AddNoteButton
    //         onPress={() =>
    //           navigation?.navigate(screens.content.addNote, {
    //             news: {
    //               ...item,
    //               isSummary: getHeaderTitle,
    //               isRecommendation,
    //               isRecommendationByTab,
    //             },
    //           })
    //         }
    //         style={{width: wdp(37)}}>
    //         <IconContainer disabled isPrimary>
    //           <Icon
    //             name="plus"
    //             size={theme.text.s55}
    //             color={theme.text.light}
    //           />
    //         </IconContainer>
    //         <AddNoteText bold>{tr('screens.addNotePage')}</AddNoteText>
    //       </AddNoteButton> */}
    //       <Row>
    //         {/* <FavoriteButtonContainer
    //           item={item}
    //           getHeaderTitle={getHeaderTitle}
    //           isRecommendation={isRecommendation}
    //           isRecommendationByTab={isRecommendationByTab}
    //           theme={theme}
    //           initialFavorite={initialFavoriteState}
    //         /> */}

    //         {/* <PlayButtonContainer
    //           tableTranslationData={tableTranslationData}
    //           language={language}
    //           theme={theme}
    //         /> */}

    //         {/* {nonInteractiveActions.map(({name, onPress, key, isPrimary}) => (
    //           <IconContainer
    //             style={{marginLeft: theme.text.s12 / 2}}
    //             key={key}
    //             onPress={onPress}
    //             isSmall
    //             isPrimary={isPrimary}>
    //             <Icon
    //               name={name}
    //               size={theme.text.s55}
    //               color={isPrimary ? theme.text.light : theme.primary}
    //             />
    //           </IconContainer>
    //         ))} */}
    //         {nonInteractiveActions.map(({name, onPress, key, isPrimary}) => (
    //           <IconContainer
    //             style={{marginLeft: theme.text.s12 / 2}}
    //             key={key}
    //             onPress={onPress}
    //             isSmall
    //             isPrimary={isPrimary}>
    //             <Icon
    //               name={name}
    //               size={theme.text.s55}
    //               color={isPrimary ? theme.text.light : theme.primary}
    //             />
    //           </IconContainer>
    //         ))}
    //       </Row>
    //     </IconsContainer>
    //   </Animated.View>
    // );
  }, [
    navigation,
    item,
    getHeaderTitle,
    isRecommendation,
    isRecommendationByTab,
    wdp,
    theme,
    currentUser?.email,
    onSharePressHandler,
    openLanguagePicker,
    headerAnimStyle,
    initialFavoriteState,
    tableTranslationData,
    language,
  ]);

  // Full summary component for bottom sheet
  const SeeAllSummary = useCallback(() => {
    const content =
      tableTranslationData?.description || tableTranslationData?.summary || '';
    const isRtl = isLanguageRTL(language) && !I18nManager.isRTL;

    return (
      <BottomSheetScrollView>
        <AutoHeightWebView
          style={{width: '100%'}}
          source={{
            html: renderArticleContent(content, {
              theme,
              isArticle: true,
              isSeeMoreArticle: true,
              isRTL: isRtl,
              fontSize: font?.fontSize,
            }),
          }}
          scrollEnabled={false}
          onShouldStartLoadWithRequest={(event: any) => {
            if (isUrl(event?.url)) {
              openUrl(event.url);
              return false;
            }
            return true;
          }}
          injectedJavaScript="window.ReactNativeWebView.postMessage(document.body.scrollHeight)"
        />
        <Space bottomInset={bottomInset} extraSpace={hdp(6)} />
      </BottomSheetScrollView>
    );
  }, [tableTranslationData, language, theme, font, bottomInset, hdp]);

  // Main content renderer
  const renderContent = () => {
    // Force RTL for RTL languages when app is not in RTL mode
    const forceRTL =
      !isLoading && isLanguageRTL(language) && !I18nManager.isRTL;

    // Show error state
    if (isError) {
      return (
        <Animated.View
          {...(Platform.OS === 'web'
            ? {className: 'screen-fade-in'}
            : {entering: FadeIn.duration(300)})}
          style={{width: '100%'}}>
          <EmptyContainer hdp={hdp}>
            <EmptyList
              iconName="alert"
              iconColor={theme.primary}
              iconSize={theme.text.s1 * 2.5}
              onRefreshPress={initPage}
              title="app.error"
              containerStyle={{
                backgroundColor: theme.primary + '22',
              }}
              showRefresh
            />
          </EmptyContainer>
        </Animated.View>
      );
    }

    // Show loading skeleton
    if (isLoading) {
      return (
        <Animated.View
          {...(Platform.OS === 'web'
            ? {className: 'screen-fade-in'}
            : {entering: FadeIn.duration(200)})}
          style={{width: '100%'}}>
          <LatestArticleSkeleton isTable={!getHeaderTitle && !isAppleUser} />
        </Animated.View>
      );
    }

    // Article content - table view for regular articles
    if (item?.id && !getHeaderTitle && !isAppleUser) {
      return (
        <Animated.View
          style={[contentAnimStyle, {width: '100%'}]}
          {...(Platform.OS === 'web'
            ? {className: 'screen-fade-in-up'}
            : {})}>
          {renderArticleHeader()}
          <TableView
            isLanguageRTL={forceRTL}
            onCustomSeeMorePress={() =>
              toggleSettingsModal({
                title:
                  tableTranslationData?.tableTranslations?.summary || 'Summary',
                component: SeeAllSummary,
              })
            }
            {...tableTranslationData}
          />
        </Animated.View>
      );
    }

    // WebView content for summaries and Apple accounts
    return (
      <Animated.View
        style={[contentAnimStyle, {width: '100%', flex: 1}]}
        {...(Platform.OS === 'web'
          ? {className: 'screen-fade-in-up'}
          : {})}>
        {renderArticleHeader()}
        <AutoHeightWebView
          style={{width: '100%', flex: 1}}
          source={{
            html: renderArticleContent(
              isAppleUser
                ? tableTranslationData?.description ||
                    tableTranslationData?.summary ||
                    ''
                : tableTranslationData?.description ||
                    tableTranslationData?.summary ||
                    item?.description ||
                    item?.summary ||
                    '',
              {
                theme,
                isArticle: true,
                isRTL: forceRTL,
                fontSize: font?.fontSize,
              },
            ),
          }}
          scrollEnabled={false}
          onShouldStartLoadWithRequest={(event: any) => {
            if (isUrl(event?.url)) {
              openUrl(event.url);
              return false;
            }
            return true;
          }}
          injectedJavaScript="window.ReactNativeWebView.postMessage(document.body.scrollHeight)"
        />
      </Animated.View>
    );
  };

  // Memoized title component
  const TitleComponent = useMemo(() => {
    if (!tableTranslationData?.title && !item?.title) return null;

    return (
      <Title
        forceRTL={!isLoading && isLanguageRTL(language) && !I18nManager.isRTL}
        bold
        font={font}>
        {tableTranslationData?.title || item?.title || ''}
      </Title>
    );
  }, [tableTranslationData?.title, item?.title, isLoading, language, font]);

  // Memoized entire component render
  return (
    <MainLayout
      enableScroll={Platform.OS === 'web'}
      backHeader
      title=""
      noPaddingHeader
      noPadding>
      <Container responsiveWidth={getByScreenSize('100%', '90%')}>
        <ContentContainer>
          <Animated.View style={{width: '100%', opacity: 1}}>
            {TitleComponent}
          </Animated.View>

          {renderContent()}

          {!isAppleUser && !isLoading && !isError && <TipButtonContainer />}

          {!isWeb && <Space bottomInset={bottomInset} extraSpace={hdp(10)} />}
        </ContentContainer>
      </Container>
      <LanguagePickerModal />
    </MainLayout>
  );
};

const mapStateToProps = (state: RootState) => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(LatestArticle);
